import { ChangeEvent, ReactElement } from "react";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import { ManageDealEditor, ManageDealOption } from "../../../Services/ManageDealmaker";

const DEAL_VALUES = [
  "Less than $10 million",
  "$10m - $50m",
  "$50m - $100m",
  "$100m - $500m",
  "$500m - $1bn",
  "More than $1bn",
  "Confidential",
];

export const NOTIFY_REGIONS = [
  { id: 9, name: "Global" },
  { id: 5, name: "Europe" },
  { id: 1, name: "Africa" },
  { id: 4, name: "Central America" },
  { id: 2, name: "Asia Pacific" },
  { id: 7, name: "North America" },
  { id: 3, name: "Caribbean" },
  { id: 8, name: "South America" },
  { id: 6, name: "Middle East" },
];

interface DealBasicsProps {
  editor: ManageDealEditor;
  countries: ManageDealOption[];
  dealContactId: number;
  openEnded: boolean;
  dateFrom: string;
  dateTo: string;
  title: string;
  dealTypeId: number;
  customDealType: string;
  dealAmount: string;
  governingLaw: string;
  regionIds: number[];
  countryIds: number[];
  onContact: (value: number) => void;
  onOpenEnded: (value: boolean) => void;
  onDateFrom: (value: string) => void;
  onDateTo: (value: string) => void;
  onTitle: (value: string) => void;
  onDealType: (value: number) => void;
  onCustomType: (value: string) => void;
  onAmount: (value: string) => void;
  onLaw: (value: string) => void;
  onRegions: (event: ChangeEvent<HTMLSelectElement>) => void;
  onCountries: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export function DealBasics(props: DealBasicsProps): ReactElement {
  return (
    <>
      <label className="inputLabel">Contact Name *</label>
      <select className="border border-gray-300 p-2" value={props.dealContactId || ""} onChange={(event) => props.onContact(Number(event.target.value))}>
        <option value="" />
        {props.editor.contacts.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select>
      <label className="inputLabel"><input type="checkbox" checked={props.openEnded} onChange={(event) => props.onOpenEnded(event.target.checked)} /> Open / No End Date</label>
      <div className="flex flex-wrap gap-4">
        <input type="date" disabled={props.openEnded} value={props.dateFrom} onChange={(event) => props.onDateFrom(event.target.value)} />
        <input type="date" disabled={props.openEnded} value={props.dateTo} onChange={(event) => props.onDateTo(event.target.value)} />
      </div>
      <label className="inputLabel">Deal Title *</label>
      <input type="text" value={props.title} maxLength={250} placeholder="Deal Title" onChange={(event) => props.onTitle(event.target.value)} />
      <label className="inputLabel">Deal Type *</label>
      <select className="border border-gray-300 p-2" value={props.dealTypeId || ""} onChange={(event) => props.onDealType(Number(event.target.value))}>
        <option value="">Select Deal Type</option>
        {props.editor.dealTypes.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
        <option value="99">Add Type</option>
      </select>
      {props.dealTypeId === 99 ? <input type="text" value={props.customDealType} maxLength={40} onChange={(event) => props.onCustomType(event.target.value)} /> : null}
      <label className="inputLabel">Deal Value *</label>
      <select className="border border-gray-300 p-2" value={props.dealAmount} onChange={(event) => props.onAmount(event.target.value)}>
        <option value="">Select Amount</option>
        {DEAL_VALUES.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
      <label className="inputLabel">Governing Law * (Separate each location with a comma)</label>
      <input type="text" value={props.governingLaw} maxLength={200} placeholder="Governing Law" onChange={(event) => props.onLaw(event.target.value)} />
      <label className="inputLabel">Deal Location * (multiple selections allowed)</label>
      <select multiple className="border border-gray-300 p-2" value={props.regionIds.map(String)} onChange={props.onRegions}>
        {props.editor.regions.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select>
      <label className="inputLabel">Country * (multiple selections allowed)</label>
      <select multiple className="border border-gray-300 p-2" value={props.countryIds.map(String)} onChange={props.onCountries}>
        {props.countries.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select>
    </>
  );
}

interface DealNarrativeProps {
  editor: ManageDealEditor;
  description: string;
  additionalInfo: string;
  industryIds: number[];
  allIndustries: boolean;
  notifyRegions: number[];
  onDescription: (value: string) => void;
  onAdditional: (value: string) => void;
  onAll: (checked: boolean) => void;
  onIndustry: (id: number, checked: boolean) => void;
  onNotify: (id: number) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function DealNarrative(props: DealNarrativeProps): ReactElement {
  return (
    <>
      <label className="inputLabel">Description *</label>
      <textarea className="border border-gray-300 p-3" rows={5} value={props.description} onChange={(event) => props.onDescription(event.target.value)} />
      <label className="inputLabel">Industry *</label>
      <label className="inputLabel"><input type="checkbox" checked={props.allIndustries} onChange={(event) => props.onAll(event.target.checked)} /> All</label>
      <div className="grid gap-2 md:grid-cols-3">
        {props.editor.industries.map((row) => (
          <label key={row.id} className="inputLabel">
            <input type="checkbox" checked={props.industryIds.includes(row.id)} onChange={(event) => props.onIndustry(row.id, event.target.checked)} />
            {row.name}
          </label>
        ))}
      </div>
      <label className="inputLabel">Additional Information</label>
      <textarea className="border border-gray-300 p-3" rows={4} value={props.additionalInfo} onChange={(event) => props.onAdditional(event.target.value)} />
      <p>Deal information is distributed to a group of firm contacts interested in receiving deal notification emails. Select regions below where this deal information should be distributed.</p>
      <p className="inputLabel">Region *</p>
      <div className="grid gap-2 md:grid-cols-3">
        {NOTIFY_REGIONS.map((row) => (
          <label key={row.id} className="inputLabel">
            <input type="checkbox" checked={props.notifyRegions.includes(row.id)} onChange={() => props.onNotify(row.id)} />
            {row.name}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-4">
        <NewButton text="Submit" onClick={props.onSubmit} />
        <NewButton text="Cancel" style="outlined" color="neutral" onClick={props.onCancel} />
      </div>
    </>
  );
}
